/**
 * Places in a PDF's text (its pages' text layers, PDF.js's: the same text at any zoom): a page and how many characters
 * into its text. What a selection is shared as (PdfPresence.tsx).
 */

/** A place: page `0` (its number), `1` characters into its text. */
type TextPlace = readonly [number, number];

/** A selection: where it started (`a`) and where it's held (`f`), as the reader made it (backwards too). */
export interface TextSelection {
  a: TextPlace;
  f: TextPlace;
}

const isPlace = (v: unknown): v is TextPlace =>
  Array.isArray(v) && v.length === 2 && v.every((n) => Number.isInteger(n) && (n as number) >= 0);

export function isTextSelection(v: unknown): v is TextSelection {
  const s = v as Partial<TextSelection> | null;
  return typeof s === "object" && s !== null && isPlace(s.a) && isPlace(s.f);
}

/** Page `n`'s text layer in `root` (null: not drawn, the page not near the screen). */
const layerOf = (root: Element, n: number): HTMLElement | null =>
  root.querySelector<HTMLElement>(`[data-page="${n}"] .textLayer`);

/** Where DOM point (`node`, `offset`) is, in `root`'s pages' text (null: not in one's text layer). */
function placeOf(root: Element, node: Node | null, offset: number): TextPlace | null {
  const el = node instanceof Element ? node : (node?.parentElement ?? null);
  const layer = el?.closest<HTMLElement>(".textLayer");
  const page = layer?.closest<HTMLElement>("[data-page]");
  if (!(node && layer && page && root.contains(page))) {
    return null;
  }
  const r = document.createRange();
  r.setStart(layer, 0);
  r.setEnd(node, offset);
  return [Number(page.dataset["page"]), r.toString().length];
}

/** What's selected in `root`'s pages' text (null: nothing, or not there). */
export function selectionIn(root: Element): TextSelection | null {
  const s = document.getSelection();
  if (!s || s.isCollapsed || s.rangeCount === 0) {
    return null;
  }
  const a = placeOf(root, s.anchorNode, s.anchorOffset);
  const f = placeOf(root, s.focusNode, s.focusOffset);
  return a && f ? { a, f } : null;
}

/** The DOM point `n` characters into `layer`'s text (its end, past it). */
function pointIn(layer: HTMLElement, n: number): { node: Node; offset: number } {
  const walk = document.createTreeWalker(layer, NodeFilter.SHOW_TEXT);
  let left = n;
  let last: Text | null = null;
  for (let t = walk.nextNode() as Text | null; t; t = walk.nextNode() as Text | null) {
    if (left <= t.length) {
      return { node: t, offset: left };
    }
    left -= t.length;
    last = t;
  }
  return last ? { node: last, offset: last.length } : { node: layer, offset: layer.childNodes.length };
}

const before = (x: TextPlace, y: TextPlace) => x[0] < y[0] || (x[0] === y[0] && x[1] <= y[1]);

/**
 * The boxes (client px) selection `s` covers in `root`'s pages at `zoom`, those drawn (its text on others isn't). A
 * page's text is laid out at the zoom it was last drawn at (`--total-scale-factor`) until a new one settles, its page
 * stretched meanwhile: its boxes stretched with it, from its top left corner.
 */
export function selectionRects(root: Element, s: TextSelection, zoom: number): DOMRect[] {
  const [from, to] = before(s.a, s.f) ? [s.a, s.f] : [s.f, s.a];
  const out: DOMRect[] = [];
  for (let n = from[0]; n <= to[0]; n += 1) {
    const layer = layerOf(root, n);
    if (!layer) {
      continue;
    }
    const start = n === from[0] ? pointIn(layer, from[1]) : { node: layer, offset: 0 };
    const end = n === to[0] ? pointIn(layer, to[1]) : { node: layer, offset: layer.childNodes.length };
    const r = document.createRange();
    r.setStart(start.node, start.offset);
    r.setEnd(end.node, end.offset);
    const drawn = Number.parseFloat(layer.style.getPropertyValue("--total-scale-factor")) || zoom;
    const k = zoom / drawn;
    const o = layer.getBoundingClientRect();
    for (const b of r.getClientRects()) {
      if (b.width > 0 && b.height > 0) {
        out.push(new DOMRect(o.left + (b.left - o.left) * k, o.top + (b.top - o.top) * k, b.width * k, b.height * k));
      }
    }
  }
  return out;
}
